import { Injectable, OnDestroy, OnInit } from "@angular/core";
import { LoggerService } from "@bfx/logger";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subject, Subscription } from "rxjs";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
const logger = LoggerService.for(__dirname);

import {
  ActiveUser,
  ActiveUserProfilePromiseService,
  CubiqWorker,
  HedgeOrderDataPrimaryLookupService,
  OrderAcceptancePromiseService,
  OrderDataActionPromiseService,
  OrderDataPromiseLookupService,
} from "shared/cubiq-services";

import { BfxErrorHandlingService } from "app/bfx-common/services/bfx-error-handling.service";
import { BfxDialogCommand } from "app/bfx-dialogs/models/bfx-dialog-command.enum";
import { BfxDialogData } from "app/bfx-dialogs/models/bfx-dialog-data.model";
import { BFXDialogIntent } from "app/bfx-dialogs/models/bfx-dialog-intent.enum";
import { FillNowExecutionState } from "app/bfx-order-fill-now/models/FillNowExecutionState.enum";
import {
  OrderFillNowCmd,
  OrderFillNowCmdPayload,
} from "app/bfx-order-fill-now/models/OrderFillNowCmdPayload";
import { OrderFillNowModel } from "app/bfx-order-fill-now/models/OrderFillNowModel";
import { CalloutModel } from "app/bfx-element-library/models/CalloutModel";

const EXT_TERMINAL_STATES: Array<CubiqTS.OrderState> = [
  CubiqTS.OrderState.EXPIRED,
  CubiqTS.OrderState.CANCELLED,
  CubiqTS.OrderState.FILLED,
  CubiqTS.OrderState.PENDING_FWDS,
  CubiqTS.OrderState.CANCEL_PENDING_FWDS,
  CubiqTS.OrderState.EXPIRY_PENDING_FWDS,
];
const REJECT_PROCESS_DELAY_MS: number = 500;

/**
 * This services acts a mediator/controller for all Fill Now dialogs
 */
@Injectable()
export class BfxOrderFillNowService implements OnInit, OnDestroy {
  private subscribedOrders: Map<string, OrderFillNowModel>;
  private activeOrders: Map<string, OrderFillNowModel>;
  private postRejectNotification: Map<string, CubiqTS.OrderLifeCycleEventImpl>;

  private orderActionDialogSource = new Subject<BfxDialogData>();
  // Observable stream for presenting dialogs
  public dialogCtrl$ = this.orderActionDialogSource.asObservable();

  private interfaceCmdEventSource = new Subject<OrderFillNowCmdPayload>();
  /** Observable stream for all model changes */
  public interfaceCmd$: Observable<OrderFillNowCmdPayload> =
    this.interfaceCmdEventSource.asObservable();

  private activeUserCd: string;
  private subscriptions: Array<Subscription> = [];

  /** Definitions of all lifecycle events subscriptions required once we start Fill Now flow */
  private readonly eventSubscriptions = [
    {
      eventName: CubiqTS.OrderLifeCycleEventImpl.name,
      eventHandler: (event: CubiqTS.OrderLifeCycleEventImpl) =>
        this.processOrderLifecycleEvent(event),
    },
    {
      eventName: CubiqTS.BulkOrderLifeCycleEventImpl.name,
      eventHandler: (event: CubiqTS.BulkOrderLifeCycleEventImpl) =>
        this.processBulkOrderLifecycleEvent(event),
    },
    {
      eventName: CubiqTS.LockLifeCycleEventImpl.name,
      eventHandler: (event: CubiqTS.LockLifeCycleEventImpl) =>
        this.processLockLifecycleEvent(event),
    },
    {
      eventName: CubiqTS.BenchmarkLockLifeCycleEventImpl.name,
      eventHandler: (event: CubiqTS.BenchmarkLockLifeCycleEventImpl) =>
        this.processLockLifecycleEvent(event),
    },
  ];

  constructor(
    private translate: TranslateService,
    private orderActionService: OrderDataActionPromiseService,
    private errorHandler: BfxErrorHandlingService,
    protected cubiqWorker: CubiqWorker,
    activeUserService: ActiveUserProfilePromiseService,
    private orderAcceptanceService: OrderAcceptancePromiseService,
    private orderLookup: OrderDataPromiseLookupService,
    private hedgeOrderLookup: HedgeOrderDataPrimaryLookupService,
  ) {
    this.initializeSubscriptions();
    activeUserService.getActiveUserData().then((value: ActiveUser) => {
      this.activeUserCd = value.userCode;
    });
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }

  /**
   * Need to listen to all order lifecycle events
   */
  private initializeSubscriptions(): void {
    this.subscribedOrders = new Map();
    this.activeOrders = new Map();
    this.postRejectNotification = new Map();
  }

  /**
   * Request Fillnow lock for the order and emit changes on the $interfaceCmd observable
   * @param orderId
   */
  public lockOrderForFillNow(orderId: string, order: CubiqTS.OrderData): OrderFillNowModel {
    logger.out.info(`ofn-s - lockOrderForFillNow() requested for order ${orderId}`);
    let model = this.activeOrders.get(orderId);
    if (!model) {
      model = new OrderFillNowModel(orderId);
      this.activeOrders.set(orderId, model);
    }

    const oldState = model.executionState;
    model.order = order;
    model.updateExecutionState(FillNowExecutionState.LOCKING);
    model.actionCallout = null;

    this.notifyUpdate(model);
    this.subscribeOrderEvents(model);
    this.orderActionService.fillNowLock(orderId).then(
      (response: CubiqTS.FillNowLockResponse | CubiqTS.Errors) => {
        if (response instanceof CubiqTS.ErrorsImpl) {
          model.updateExecutionState(FillNowExecutionState.UNLOCKED_FAIL);
          this.notifyActionError(model, response);
        } else if (response instanceof CubiqTS.FillNowLockResponseImpl) {
          if (response.errors) {
            let state = this.getOrderTerminalState(orderId);

            if (state != null) {
              model.updateExecutionState(FillNowExecutionState.UNLOCKED_TERMINAL);
              this.notifyActionError(model, this.getTerminalRejectResult(state));
            } else {
              model.updateExecutionState(FillNowExecutionState.UNLOCKED_FAIL);
              this.notifyActionError(model, response.errors);
            }
          } else {
            model.updateExecutionState(FillNowExecutionState.LOCKED);
            model.setLockOrderAmount(response.orderAmt);
            model.actionCallout = null;
            this.notifyUpdate(model);
          }
        }
      },
      (error) => {
        model.updateExecutionState(FillNowExecutionState.UNLOCKED_FAIL);
        this.notifyActionError(model, error);
      },
    );

    return model;
  }

  /**
   * Request Fillnow unlock for the order and emit changes on the $interfaceCmd observable
   * @param orderId
   */
  public unlockOrderForFillNow(model: OrderFillNowModel): void {
    logger.out.info(`ofn-s - unlockOrderForFillNow() requested for order ${model.orderId}`);
    const order = model.order;
    if (order.lockType !== CubiqTS.LockType.FILL_NOW_LOCK) {
      model.actionCallout = {
        calloutType: "negative",
        calloutMessage: this.translate.instant("fill_now.callout.unlock_error"),
        calloutTooltip: null,
      };
      this.notifyUpdate(model);
      return;
    }

    const oldState = model.executionState;
    model.updateExecutionState(FillNowExecutionState.UNLOCKING);
    this.notifyUpdate(model);
    this.orderActionService.fillNowUnlock([model.orderId]).then(
      (response: any) => {
        if (response instanceof CubiqTS.ErrorsImpl) {
          model.updateExecutionState(oldState);
          this.notifyActionError(model, response);
        } else if (response instanceof CubiqTS.FillNowLockResponseImpl) {
          if (response.errors) {
            model.updateExecutionState(oldState);
            this.notifyActionError(model, response.errors);
          } else {
            model.updateExecutionState(FillNowExecutionState.UNLOCKED_SUCCESS);
            model.actionCallout = null;
            this.notifyUpdate(model);
          }
        }
      },
      (error) => {
        model.updateExecutionState(oldState);
        this.notifyActionError(model, error);
      },
    );
  }

  /**
   * Execute Fill Now on the order for the specified amount
   * @param orderId
   */
  public fillNowPartial(model: OrderFillNowModel, fillAmount: CubiqTS.BigNumber): void {
    logger.out.info(
      `ofn-s - fillNow() requested for partial amount ${fillAmount} for order ${model.orderId}`,
    );
    this.fillNow(model, fillAmount);
  }

  /**
   * Execute Fill Now on the order for the complete amount
   * @param orderId
   */
  public fillNowFull(model: OrderFillNowModel): void {
    logger.out.info(`ofn-s - fillNow() requested for full amount for order ${model.orderId}`);
    this.fillNow(model, null);
  }

  /**
   * Execute Fill Now on the order for the specified amount - if amount is null, then entire amount is filled
   * @param model
   * @param fillAmount
   * @returns
   */
  public fillNow(model: OrderFillNowModel, fillAmount: CubiqTS.BigNumber): void {
    if (!model || model.executionState !== FillNowExecutionState.LOCKED) {
      model.actionCallout = new CalloutModel(
        "negative",
        this.translate.instant("fill_now.callout.fill_now_error"),
        this.translate.instant("fill_now.callout.fill_now_not_locked"),
      );
      this.notifyUpdate(model);
      return;
    }

    model.updateExecutionState(FillNowExecutionState.FILLING);
    model.actionCallout = new CalloutModel(
      "info",
      this.translate.instant("fill_now.callout.external_acceptance_needed"),
      this.translate.instant("fill_now.callout.sent_external"),
    );
    this.notifyUpdate(model);
    this.subscribeOrderEvents(model);
    this.orderActionService.fillNow(model.orderId, fillAmount).then(
      (response: CubiqTS.OrderFillNowResponse | CubiqTS.ErrorsImpl) => {
        if (response instanceof CubiqTS.ErrorsImpl) {
          model.updateExecutionState(FillNowExecutionState.LOCKED);
          this.notifyActionError(model, response);
        } else if (response instanceof CubiqTS.OrderFillNowResponseImpl) {
          if (response.errors) {
            model.updateExecutionState(FillNowExecutionState.LOCKED);
            this.notifyActionError(model, response.errors);
          }
        }
      },
      (error) => {
        model.updateExecutionState(FillNowExecutionState.LOCKED);
        this.notifyActionError(model, error);
      },
    );
  }

  public forceAccept(order: CubiqTS.OrderData): Promise<any[]> {
    return this.orderAcceptanceService.requestAcceptance(
      [order.orderId.toString()],
      "",
      CubiqTS.AcceptanceAction.ACCEPT,
      CubiqTS.AcceptanceType.ENTRY,
      null,
      null,
      order.orderType,
      order.fixingDetails?.bmOrderType,
    );
  }

  public forceReject(order: CubiqTS.OrderData): Promise<any[]> {
    return this.orderAcceptanceService.requestAcceptance(
      [order.orderId.toString()],
      "",
      CubiqTS.AcceptanceAction.REJECT,
      CubiqTS.AcceptanceType.ENTRY,
      null,
      null,
      order.orderType,
      order.fixingDetails?.bmOrderType,
    );
  }

  /**
   * Process an error for the specified action event
   * @param event
   * @param response
   */
  private notifyActionError(model: OrderFillNowModel, response: any) {
    let errorMessage: string;
    if (response instanceof CubiqTS.ErrorsImpl) {
      errorMessage = this.errorHandler.getErrorMessages(response).join(",");
    } else {
      errorMessage = response?.toString() ?? "<Unknown Error>";
    }
    logger.out.warn(`ofn-s - got error for order ${model.orderId} : ${errorMessage}`);

    model.actionCallout = new CalloutModel(
      "negative",
      this.translate.instant("fill_now.callout.request_rejected"),
      errorMessage,
    );
    this.notifyUpdate(model);
  }

  /**
   * Show an external error dialog - not sure if this is needed when callouts are used
   * @param errorMessage
   */
  private showErrorDialog(errorMessage: string) {
    let payload = {
      messages: [this.translate.instant("blotter.action.generic_error.desc", [errorMessage])],
      title: "blotter.action.generic_error.title",
      showConfirmationMessage: false,
      showLogoutMessage: false,
      intent: BFXDialogIntent.NEGATIVE,
    };

    const dialogData = new BfxDialogData(BfxDialogCommand.DIALOG_GENERIC_OK, null, null, payload);
    this.orderActionDialogSource.next(dialogData);
  }

  /**
   * Show an external error dialog - not sure if this is needed when callouts are used
   * @param errorMessage
   */
  public showExternalWarningDialog(acceptance: boolean = false): Promise<boolean> {
    const message = this.translate.instant(
      acceptance
        ? "order_entry.dialog.force.externalOrderWarning.message"
        : "order_entry.dialog.close.externalOrderWarning.message",
    );

    const subMessage = this.translate.instant(
      acceptance
        ? "order_entry.dialog.force.externalOrderWarning.subMessage"
        : "order_entry.dialog.close.externalOrderWarning.subMessage",
    );

    const title = "order_entry.dialog.close.externalOrderWarning.title";

    return new Promise<boolean>((res) => {
      let payload: any = {
        title: title,
        messages: [message],
        subMessage: subMessage,
        callback: () => res(true),
        callbackNegative: () => res(false),
      };
      const dialogData = new BfxDialogData(
        BfxDialogCommand.DIALOG_GENERIC_CONFIRM_OPEN,
        null,
        null,
        payload,
      );
      this.orderActionDialogSource.next(dialogData);
    });
  }

  /**
   * Remove subscription for the order - stop tracking order changes
   * @param orderId
   */
  public unsubscribeOrder(orderId: string) {
    this.activeOrders.delete(orderId);
    this.unsubscribeOrderEvents(orderId);
  }

  processBulkOrderLifecycleEvent(event: CubiqTS.BulkOrderLifeCycleEventImpl): void {
    event.events.forEach((e) => this.processOrderLifecycleEvent(e));
  }

  /**
   *  Process a raw lifecycle event - it must be the latest for the order
   * @param event
   * @returns
   */
  processOrderLifecycleEvent(event: CubiqTS.OrderLifeCycleEventImpl): void {
    if (this.subscribedOrders.size == 0 || !event.orderData?.orderId) {
      return;
    }
    const order = event.orderData;
    const orderId = order.orderId.toString();

    // logger.out.info(`ofn-s: Order UPDATE ${orderId}`);
    const model = this.subscribedOrders.get(orderId);
    if (model) {
      logger.out.info(`ofn-s: found order subscription for ${orderId}`);

      if (event.orderData?.orderState === CubiqTS.OrderState.REJECTED) {
        logger.out.debug(
          `ofn-s: delaying reject processing of ${orderId} for ${REJECT_PROCESS_DELAY_MS}`,
        );

        // We delay processing rejects until we figure out if we are reverting to a terminal
        // state - we'll buffer LC events for this order and check latest after interval
        model.postReject = true;
        setTimeout(() => {
          this.processReject(event);
        }, REJECT_PROCESS_DELAY_MS);
        return;
      } else if (model.postReject) {
        logger.out.debug(`ofn-s: model in post reject: ${orderId}, ${event.orderData?.orderState}`);
        // Buffer this event for processing after reject timeout
        this.postRejectNotification.set(event.orderId, event);
        return;
      }

      const oldItem: CubiqTS.OrderDataImpl = model.order;
      if (oldItem != null && order.txTime != null && oldItem.txTime != null) {
        if (
          order.txTime.isBefore(oldItem.txTime) ||
          (order.txTime.isSame(oldItem.txTime) && !event.rollbackEventFg)
        ) {
          logger.out.info(
            `ofn-s - Ignoring lifecycle event for order ${orderId} as txTime fails check`,
          );
          return;
        }
      }

      model.order = order;
      if (event.errors?.errorMessages?.length > 0) {
        // If state is one of the terminal states coming from external system
        // than hide the edit button
        let terminal: boolean = EXT_TERMINAL_STATES.includes(order.orderState);
        model.updateExecutionState(
          terminal ? FillNowExecutionState.UNLOCKED_TERMINAL : FillNowExecutionState.UNLOCKED_FAIL,
        );
        this.notifyActionError(model, event.errors);
      } else {
        this.updateOrder(model, order);
      }
    }
  }

  /**
   * Handle a change in the lock state of a monitored order
   * @param event
   * @returns
   */
  private processLockLifecycleEvent(event: CubiqTS.LockLifeCycleEventImpl): void {
    if (this.subscribedOrders.size == 0 || !event.orderIds || event.orderIds.length == 0) {
      logger.out.warn("ofn-s: No IDs found in incoming lock event");
      return;
    }
    // Work off the order Ids and the event contents
    event.orderIds.forEach((orderId) => {
      const model = this.subscribedOrders.get(orderId);
      if (model && model.order) {
        logger.out.info(`ofn-s: found order subscription for Lock event for ${orderId}`);
        model.order.lockType = event.lockType;
        model.order.lockUserId = event.lockUserId;
        this.updateOrder(model, model.order);
      }
    });
  }

  /**
   * Update the model with the new Order and notify all view subscribers
   * @param model
   * @param order
   */
  private updateOrder(model: OrderFillNowModel, order: CubiqTS.OrderData) {
    if (
      model.executionState === FillNowExecutionState.FILLING ||
      model.executionState === FillNowExecutionState.EXT_ACCEPTANCE
    ) {
      switch (order.orderState) {
        case CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED:
          model.updateExecutionState(FillNowExecutionState.EXT_ACCEPTANCE);
          this.notifyUpdate(model);
          break;
        case CubiqTS.OrderState.ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.REJECTED:
          break;
        case CubiqTS.OrderState.EXPIRED:
        case CubiqTS.OrderState.CANCELLED:
        case CubiqTS.OrderState.FILLED:
          model.updateExecutionState(FillNowExecutionState.UNLOCKED_TERMINAL);
          let result = this.getTerminalRejectResult(order?.orderState, order.orderErrors);
          this.notifyActionError(model, result);
          break;
        default:
          // We have submitted the fillNow - now we process the result
          model.updateExecutionState(FillNowExecutionState.UNLOCKED_SUCCESS);
          model.actionCallout = new CalloutModel(
            "positive",
            this.translate.instant("fill_now.callout.amend_success"),
            this.translate.instant("fill_now.callout.operation_success"),
          );
          this.notifyUpdate(model);
          break;
      }
    } else if (
      order.lockType !== CubiqTS.LockType.FILL_NOW_LOCK &&
      model.executionState === FillNowExecutionState.LOCKED
    ) {
      // Fill now lock has been removed
      let executionState = FillNowExecutionState.UNLOCKED_EXTERNAL;

      if (EXT_TERMINAL_STATES.includes(order.orderState)) {
        executionState = FillNowExecutionState.UNLOCKED_TERMINAL;
      }

      model.updateExecutionState(executionState);

      const calloutMsgId: string = EXT_TERMINAL_STATES.includes(order.orderState)
        ? "fill_now.callout.inappropriate_state"
        : "fill_now.callout.unlocked_external";
      model.actionCallout = new CalloutModel("info", this.translate.instant(calloutMsgId), "");

      this.notifyUpdate(model);
    } else if (EXT_TERMINAL_STATES.includes(order.orderState)) {
      model.updateExecutionState(FillNowExecutionState.UNLOCKED_TERMINAL);
      let result = this.getTerminalRejectResult(order?.orderState, order.orderErrors);
      this.notifyActionError(model, result);
    }
  }

  /**
   * Emit model as change event for any subscribed view components
   * @param model
   */
  private notifyUpdate(model: OrderFillNowModel) {
    // If the model is in a terminal state we can unsubscribe from all updates
    switch (model.executionState) {
      case FillNowExecutionState.UNLOCKED_SUCCESS:
      case FillNowExecutionState.UNLOCKED_TERMINAL:
      case FillNowExecutionState.UNLOCKED_FAIL:
      case FillNowExecutionState.UNLOCKED_EXTERNAL:
        this.unsubscribeOrder(model.orderId);
        break;
      default:
        break;
    }

    const payload = new OrderFillNowCmdPayload(OrderFillNowCmd.UPDATE_MODEL, model.orderId, model);
    this.interfaceCmdEventSource.next(payload);
  }

  /**
   * Called in case of reject lifecycle event coming from server after a given interval
   * defined in DEBOUNCE_REJECT. The method than determines the execution state of the model
   * based on the last received lifecycle event.
   *
   * @param rejectNotif the reject notification itself
   */
  private processReject(rejectNotif: CubiqTS.OrderLifeCycleEventImpl): void {
    let orderId = rejectNotif.orderId;
    const model = this.subscribedOrders.get(orderId);

    if (model === null) {
      logger.out.warn(`ofn-s: post reject missing model for order ${orderId}`);
    } else if (model.postReject === true) {
      model.postReject = false;

      let latestNotification = this.postRejectNotification.get(orderId);

      if (latestNotification != null) {
        let orderState = latestNotification.orderData?.orderState;
        let terminal: boolean = EXT_TERMINAL_STATES.includes(orderState);

        model.updateExecutionState(
          terminal ? FillNowExecutionState.UNLOCKED_TERMINAL : FillNowExecutionState.UNLOCKED_FAIL,
        );

        let result: CubiqTS.ErrorsImpl | string;

        if (terminal) {
          result = this.getTerminalRejectResult(orderState, rejectNotif.errors);
        } else {
          result = rejectNotif.errors;
        }
        this.notifyActionError(model, result);
      }
    } else {
      logger.out.warn(`ofn-s: post model not in expected state for order ${orderId}`);
    }

    this.postRejectNotification.delete(orderId);
  }

  /**
   * Helper method for constructing the tooltip message for an order that is in a terminal
   * state for fill now
   * @param orderState the state of the order
   * @returns the message shown in the tooltip of the callout
   */
  private getTerminalRejectResult(
    orderState: CubiqTS.OrderState,
    errors: CubiqTS.ErrorsImpl = null,
  ): string {
    const errorMsgs = this.errorHandler.getErrorMessages(errors).join(",");

    const errorId =
      errorMsgs.length > 0
        ? "fill.now.order.in.terminal.state.errors"
        : "fill.now.order.in.terminal.state";
    let stateName = [this.translate.instant(`order.state.${CubiqTS.OrderState[orderState]}`)];
    return this.translate.instant(errorId, [stateName, errorMsgs]);
  }

  /**
   * Attempts to determine the order state by looking it up in the
   * lookup services
   * @param orderId id of the order
   * @returns true if order is found and in a terminal state for fill now, false otherwise
   */
  private getOrderTerminalState(orderId: string): CubiqTS.OrderState {
    let result: CubiqTS.OrderState = null;

    try {
      let order = this.orderLookup.getBase().getItem(orderId);

      if (order === null) {
        this.hedgeOrderLookup.getBase().getItem(orderId);
      }

      if (order != null) {
        result = EXT_TERMINAL_STATES.includes(order.orderState) ? order.orderState : null;
      }
    } catch (e) {
      logger.out.warn(
        `${this.constructor.name}: Error determining if order state is terminal ` +
          `for order ${orderId}, ${e}`,
        e,
      );
    }

    return result;
  }

  private subscribeOrderEvents(model: OrderFillNowModel) {
    if (model) {
      this.subscribedOrders.set(model.orderId, model);

      if (this.subscriptions.length == 0) {
        const subs: Array<{ eventName: string; eventHandler: any }> = this.eventSubscriptions;
        this.subscriptions = subs.map((sub) => {
          return this.cubiqWorker.getEncodableObservable(sub.eventName).subscribe(sub.eventHandler);
        });
      }
    }
  }

  private unsubscribeOrderEvents(orderId: string) {
    if (orderId) {
      this.subscribedOrders.delete(orderId);
      if (this.subscribedOrders.size === 0) {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.subscriptions.length = 0;
      }
    }
  }
}
